import { useId, useMemo, type CSSProperties } from 'react';

export type OrbMode = 'tone' | 'ink-light' | 'ink-dark';

interface OrbProps {
  mode?: OrbMode;
  ink?: string;
  lat?: number;
  lon?: number;
  className?: string;
  style?: CSSProperties;
}

const L0: [number, number, number] = [-0.42, 0.52, 0.74];
const LN = Math.hypot(L0[0], L0[1], L0[2]);
const L: [number, number, number] = [L0[0] / LN, L0[1] / LN, L0[2] / LN];

interface Face {
  points: string;
  lum: number;
  z: number;
}

function buildFaces(latN: number, lonN: number): Face[] {
  const cx = 80;
  const cy = 80;
  const R = 64;
  const faces: Face[] = [];
  for (let i = 0; i < latN; i++) {
    const t0 = Math.PI * (i / latN);
    const t1 = Math.PI * ((i + 1) / latN);
    for (let j = 0; j < lonN; j++) {
      const p0 = 2 * Math.PI * (j / lonN);
      const p1 = 2 * Math.PI * ((j + 1) / lonN);
      const verts = ([[t0, p0], [t0, p1], [t1, p1], [t1, p0]] as const).map(([t, p]) => {
        const nx = Math.sin(t) * Math.cos(p);
        const ny = Math.cos(t);
        const nz = Math.sin(t) * Math.sin(p);
        return { n: [nx, ny, nz] as [number, number, number], x: cx + R * nx, y: cy - R * ny };
      });
      const fn = [0, 1, 2].map(
        (k) => verts.reduce((s, v) => s + v.n[k], 0) / verts.length,
      ) as [number, number, number];
      const fl = Math.hypot(fn[0], fn[1], fn[2]);
      const N: [number, number, number] = [fn[0] / fl, fn[1] / fl, fn[2] / fl];
      if (N[2] <= 0) continue;
      let lum = Math.max(0, N[0] * L[0] + N[1] * L[1] + N[2] * L[2]);
      lum = 0.05 + 0.95 * Math.pow(lum, 0.85);
      faces.push({
        points: verts.map((v) => `${v.x.toFixed(1)},${v.y.toFixed(1)}`).join(' '),
        lum,
        z: N[2],
      });
    }
  }
  faces.sort((a, b) => a.z - b.z);
  return faces;
}

export function AsaroOrb({
  mode = 'tone',
  ink,
  lat = 6,
  lon = 12,
  className,
  style,
}: OrbProps) {
  const rawId = useId();
  const clipId = `orbclip-${rawId.replace(/:/g, '')}`;
  const faces = useMemo(() => buildFaces(lat, lon), [lat, lon]);

  return (
    <svg
      viewBox="0 0 160 160"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="80" cy="80" r="64" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {faces.map((f, i) => {
          let fill: string;
          let opacity = 1;
          let stroke: string | undefined;
          let strokeWidth: number | undefined;
          if (mode === 'tone') {
            const lo = [28, 29, 34];
            const hi = [238, 240, 243];
            const c = lo.map((l, k) => Math.round(l + (hi[k] - l) * f.lum));
            fill = `rgb(${c[0]},${c[1]},${c[2]})`;
            stroke = 'rgba(0,0,0,0.14)';
            strokeWidth = 0.5;
          } else if (mode === 'ink-light') {
            fill = ink ?? '#0c0c10';
            opacity = 0.1 + (1 - f.lum) * 0.86;
          } else {
            fill = ink ?? '#f5f5f7';
            opacity = 0.12 + f.lum * 0.88;
          }
          return (
            <polygon
              key={i}
              points={f.points}
              fill={fill}
              opacity={opacity}
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeLinejoin="round"
            />
          );
        })}
      </g>
    </svg>
  );
}
